import { useEffect, useMemo, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { createXRStore, XR, XRButton } from '@react-three/xr';
import type { ConnectionState } from './types';
import { filterLogs, selectServiceList, useLogverseStore } from './store';
import { startLogStream } from './utils/logStream';
import LogverseScene from './components/LogverseScene';
import UIOverlay from './components/UIOverlay';
import XRControls from './components/XRControls';

const ORG_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{1,30}[a-z0-9])$/;
const RESERVED_ORG_SLUGS = new Set([
  'www',
  'api',
  'auth',
  'account',
  'dashboard',
  'docs',
  'blog',
  'app',
  'logs',
  'view',
  'panel',
  'space',
  'logverse'
]);

const xrStore = createXRStore({
  hand: true,
  controller: {
    teleportPointer: {}
  }
});

const parseOrgSlugFromPath = (pathname: string): string | null => {
  const firstSegment = pathname
    .split('/')
    .map((segment) => segment.trim().toLowerCase())
    .filter(Boolean)[0];

  if (!firstSegment || !ORG_SLUG_PATTERN.test(firstSegment) || RESERVED_ORG_SLUGS.has(firstSegment)) {
    return null;
  }

  return firstSegment;
};

const resolveTelemetryOrigin = (): string => {
  const candidate =
    (typeof import.meta.env.VITE_TELEMETRY_ORIGIN === 'string' ? import.meta.env.VITE_TELEMETRY_ORIGIN : '') ||
    'https://telemetry.w7s.io';
  return candidate.trim().replace(/\/+$/, '');
};

const resolveGithubAuthUrl = (): string => {
  const accountOrigin = window.location.hostname.endsWith('.w7s.cloud')
    ? 'https://account.w7s.cloud'
    : 'https://account.w7s.io';

  const url = new URL('/github/auth', accountOrigin);
  url.searchParams.set('redirect_uri', window.location.href);
  return url.toString();
};

function FpsProbe() {
  const setFps = useLogverseStore((state) => state.setFps);

  useFrame((_, delta) => {
    const fps = delta > 0 ? 1 / delta : 0;
    setFps(fps);
  });

  return null;
}

export default function App() {
  const logs = useLogverseStore((state) => state.logs);
  const paused = useLogverseStore((state) => state.paused);
  const selectedLog = useLogverseStore((state) => state.selectedLog);
  const activeService = useLogverseStore((state) => state.activeService);
  const severityFilter = useLogverseStore((state) => state.severityFilter);
  const timelinePosition = useLogverseStore((state) => state.timelinePosition);
  const fps = useLogverseStore((state) => state.fps);

  const togglePause = useLogverseStore((state) => state.togglePause);
  const toggleSeverity = useLogverseStore((state) => state.toggleSeverity);
  const showOnlySeverity = useLogverseStore((state) => state.showOnlySeverity);
  const setSelectedLog = useLogverseStore((state) => state.setSelectedLog);
  const setActiveService = useLogverseStore((state) => state.setActiveService);
  const setTimelinePosition = useLogverseStore((state) => state.setTimelinePosition);
  const addLog = useLogverseStore((state) => state.addLog);

  const orgSlug = useMemo(() => parseOrgSlugFromPath(window.location.pathname), []);
  const telemetryOrigin = useMemo(() => resolveTelemetryOrigin(), []);
  const githubAuthUrl = useMemo(() => resolveGithubAuthUrl(), []);
  const tokenStorageKey = useMemo(() => (orgSlug ? `logverse:github-token:${orgSlug}` : null), [orgSlug]);

  const [githubToken, setGithubToken] = useState('');
  const [tokenDraft, setTokenDraft] = useState('');
  const [connectionState, setConnectionState] = useState<ConnectionState>('connecting');
  const [wsUrl, setWsUrl] = useState('ws://localhost:8080/logs');

  useEffect(() => {
    if (!tokenStorageKey) {
      setGithubToken('');
      setTokenDraft('');
      return;
    }

    const stored = window.localStorage.getItem(tokenStorageKey) ?? '';
    setGithubToken(stored);
    setTokenDraft(stored);
  }, [tokenStorageKey]);

  useEffect(() => {
    const stream = startLogStream({
      orgSlug,
      githubToken,
      telemetryOrigin,
      onLog: addLog,
      onConnectionChange: setConnectionState
    });

    setWsUrl(stream.url);

    return () => {
      stream.dispose();
    };
  }, [addLog, githubToken, orgSlug, telemetryOrigin]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(target.tagName)) {
        return;
      }

      if (event.code === 'Space') {
        event.preventDefault();
        togglePause();
        return;
      }

      if (event.key === '1') {
        showOnlySeverity('info');
        return;
      }

      if (event.key === '2') {
        showOnlySeverity('warn');
        return;
      }

      if (event.key === '3') {
        showOnlySeverity('error');
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showOnlySeverity, togglePause]);

  const services = useMemo(() => selectServiceList(logs), [logs]);
  const filteredLogs = useMemo(
    () => filterLogs(logs, activeService, severityFilter),
    [logs, activeService, severityFilter]
  );

  const saveToken = () => {
    if (!tokenStorageKey) {
      return;
    }

    const nextToken = tokenDraft.trim();
    if (nextToken) {
      window.localStorage.setItem(tokenStorageKey, nextToken);
    } else {
      window.localStorage.removeItem(tokenStorageKey);
    }
    setGithubToken(nextToken);
  };

  const clearToken = () => {
    if (tokenStorageKey) {
      window.localStorage.removeItem(tokenStorageKey);
    }
    setTokenDraft('');
    setGithubToken('');
  };

  return (
    <main className="relative h-screen w-screen overflow-hidden bg-[#02040a] text-slate-100">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_10%,rgba(80,240,255,0.14),transparent_40%),radial-gradient(circle_at_90%_12%,rgba(99,115,255,0.18),transparent_35%),radial-gradient(circle_at_50%_100%,rgba(35,83,140,0.35),transparent_45%)]" />

      <div className="pointer-events-auto absolute right-4 top-4 z-30 flex max-w-sm flex-col items-end gap-2">
        <XRButton
          store={xrStore}
          mode="immersive-vr"
          className="rounded-full border border-cyan-300/40 bg-slate-900/80 px-5 py-3 font-display text-sm font-semibold uppercase tracking-[0.13em] text-cyan-100 backdrop-blur-md transition hover:border-cyan-100/70 hover:bg-cyan-300/20"
        >
          Enter Immersive Mode
        </XRButton>

        {orgSlug ? (
          <div className="w-full rounded-2xl border border-cyan-300/20 bg-slate-950/82 p-3 shadow-holo backdrop-blur-md">
            <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-cyan-100">Telemetry Auth</p>
            <p className="mb-2 mt-1 font-mono text-xs text-cyan-50">org: {orgSlug}</p>

            <div className="flex gap-2">
              <input
                type="password"
                value={tokenDraft}
                onChange={(event) => setTokenDraft(event.target.value)}
                placeholder="GitHub token for org read"
                className="w-full rounded-md border border-white/15 bg-slate-900/90 px-2 py-1 text-xs text-slate-100 outline-none ring-cyan-300/50 placeholder:text-slate-500 focus:ring"
              />
              <button
                type="button"
                onClick={saveToken}
                className="rounded-md border border-cyan-300/35 px-2 py-1 font-mono text-[11px] text-cyan-100 transition hover:bg-cyan-300/15"
              >
                Save
              </button>
            </div>

            <div className="mt-2 flex items-center gap-2">
              <a
                href={githubAuthUrl}
                className="rounded-md border border-white/20 px-2 py-1 font-mono text-[11px] text-slate-100 transition hover:border-cyan-200/60 hover:text-cyan-100"
              >
                Login with GitHub
              </a>
              <button
                type="button"
                onClick={clearToken}
                className="rounded-md border border-white/20 px-2 py-1 font-mono text-[11px] text-slate-200 transition hover:border-white/40"
              >
                Clear Token
              </button>
            </div>

            {connectionState === 'auth_required' ? (
              <p className="mt-2 font-mono text-[11px] text-amber-200">
                Auth required: login and/or provide a GitHub token with org membership.
              </p>
            ) : null}
          </div>
        ) : null}

        <p className="rounded-full bg-slate-950/60 px-3 py-1 font-mono text-[10px] text-cyan-100/90">
          Shortcuts: Space pause, 1/2/3 severity
        </p>
      </div>

      <UIOverlay
        mode="screen"
        services={services}
        activeService={activeService}
        orgSlug={orgSlug}
        totalLogs={filteredLogs.length}
        fps={fps}
        paused={paused}
        severityFilter={severityFilter}
        timelinePosition={timelinePosition}
        connectionState={connectionState}
        wsUrl={wsUrl}
        onTogglePause={togglePause}
        onToggleSeverity={toggleSeverity}
        onServiceSelect={setActiveService}
        onTimelineChange={setTimelinePosition}
      />

      <Canvas
        dpr={[1, 1.7]}
        camera={{
          position: [0, 3.6, 12],
          fov: 58,
          near: 0.1,
          far: 150
        }}
      >
        <XR store={xrStore}>
          <XRControls />
          <FpsProbe />

          <LogverseScene
            logs={filteredLogs}
            services={services}
            selectedLog={selectedLog}
            activeService={activeService}
            orgSlug={orgSlug}
            telemetryOrigin={telemetryOrigin}
            onSelectLog={setSelectedLog}
            onSelectService={setActiveService}
            timelinePosition={timelinePosition}
          />

          <UIOverlay
            mode="vr"
            services={services}
            activeService={activeService}
            orgSlug={orgSlug}
            totalLogs={filteredLogs.length}
            fps={fps}
            paused={paused}
            severityFilter={severityFilter}
            timelinePosition={timelinePosition}
            connectionState={connectionState}
            wsUrl={wsUrl}
            onTogglePause={togglePause}
            onToggleSeverity={toggleSeverity}
            onServiceSelect={setActiveService}
            onTimelineChange={setTimelinePosition}
          />
        </XR>
      </Canvas>
    </main>
  );
}
